import { useState, useMemo, useCallback } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useAdminUserReport } from "@/hooks/queries/use-admin-activity";
import { UserReportTable } from "@/components/admin/UserReportTable";
import { ExportBar } from "@/components/admin/ExportBar";
import { usePageTitle } from "@/hooks/use-page-title";
import { generateCSV, triggerPrint } from "@/lib/export-utils";
import { formatDuration } from "@/lib/format-utils";
import {
  PeriodSelector,
  defaultPeriodState,
  type PeriodState,
  type PeriodType,
} from "@/components/admin/PeriodSelector";

// ── Period → API granularity mapping ────────────────────────────────

function periodToGranularity(
  periodType: PeriodType
): "day" | "week" | "month" | undefined {
  switch (periodType) {
    case "day":
      return undefined; // single-row / default daily
    case "week":
      return "day";
    case "month":
      return "day";
    case "quarter":
      return "week";
    case "year":
      return "month";
    case "custom":
      return "day";
  }
}

// ── Period label for sub-period rows ────────────────────────────────

function granularityLabel(periodType: PeriodType): string {
  switch (periodType) {
    case "quarter":
      return "Weekly";
    case "year":
      return "Monthly";
    default:
      return "Daily";
  }
}

// ── Init period state from navigation or defaults ───────────────────

interface NavState {
  periodType?: PeriodType;
  startDate?: string;
  endDate?: string;
}

function initPeriodState(navState: NavState | null): PeriodState {
  if (
    navState?.periodType &&
    navState.startDate &&
    navState.endDate
  ) {
    return {
      periodType: navState.periodType,
      startDate: navState.startDate,
      endDate: navState.endDate,
    };
  }
  return defaultPeriodState();
}

// ── Component ───────────────────────────────────────────────────────

export function UserReportPage() {
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const navState = (location.state as NavState) ?? null;

  const [period, setPeriod] = useState<PeriodState>(() =>
    initPeriodState(navState)
  );

  const granularity = useMemo(
    () => periodToGranularity(period.periodType),
    [period.periodType]
  );

  const { data, isLoading, isError } = useAdminUserReport(
    userId ?? "",
    period.startDate,
    period.endDate,
    granularity
  );

  const pageTitle = data?.user
    ? `${data.user.name} — Activity Report`
    : "User Activity Report";
  usePageTitle(pageTitle);

  const handleDownloadCSV = useCallback(() => {
    if (!data || data.rows.length === 0) return;

    const headers = [
      "Date",
      "Epic Requests",
      "Epics Created",
      "Features Done",
      "Tasks Done",
      "Epics Done",
      "Elapsed",
      "Avg Elapsed/Item",
      "AI Session Time",
    ];

    const rows = data.rows.map((row) => [
      row.date,
      String(row.epicRequestsCreated),
      String(row.epicsCreated),
      String(row.featuresCompleted),
      String(row.tasksCompleted),
      String(row.epicsCompleted),
      formatDuration(row.totalElapsedMinutes),
      formatDuration(row.avgElapsedPerItem),
      formatDuration(row.aiSessionMinutes),
    ]);

    const userName = data.user?.name?.replace(/\s+/g, "-") ?? "user";
    const ts = new Date().toISOString().slice(0, 10);
    generateCSV(headers, rows, `user-report-${userName}-${ts}.csv`);
  }, [data]);

  return (
    <div className="pt-4 px-4 md:px-0">
      {/* Print-only header */}
      <div className="print-header hidden">
        <h1>{data?.user?.name ?? "User"} — Activity Report</h1>
        <p>
          {period.startDate} — {period.endDate} · Exported{" "}
          {new Date().toLocaleDateString()}
        </p>
      </div>

      <div className="glass-card rounded-2xl p-4 md:p-6 space-y-6">
        {/* Back navigation */}
        <div>
          <Link
            to="/admin/activity"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Activity Dashboard
          </Link>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            {isLoading && !data ? (
              <div className="h-6 w-48 rounded bg-muted/30 animate-pulse" />
            ) : data?.user ? (
              <div>
                <h1 className="text-lg font-medium">{data.user.name}</h1>
                <p className="text-sm text-muted-foreground">
                  {data.user.email}
                </p>
              </div>
            ) : (
              <h1 className="text-lg font-medium">User Activity Report</h1>
            )}
          </div>

          {/* Period selector + Export */}
          <div className="flex items-center gap-3 flex-wrap">
            {data && data.rows.length > 0 && (
              <ExportBar
                onDownloadCSV={handleDownloadCSV}
                onPrint={triggerPrint}
              />
            )}
            <PeriodSelector value={period} onChange={setPeriod} />
          </div>
        </div>

        {/* Granularity hint */}
        <p className="text-xs text-muted-foreground">
          Showing {granularityLabel(period.periodType).toLowerCase()} breakdown
        </p>

        {/* Table area */}
        {isLoading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            Loading…
          </div>
        ) : isError ? (
          <div className="py-10 text-center text-sm text-destructive">
            Failed to load report. Please try again.
          </div>
        ) : data ? (
          <UserReportTable
            rows={data.rows}
            totals={data.totals}
            periodType={period.periodType}
          />
        ) : null}
      </div>
    </div>
  );
}
